import {
  EligibilitySource,
  IdpEligibilityAction,
  IdpEligibilityErrorEnum,
  IdpEligibilityResult,
} from 'abi-lcm-common-types';

import type { LcmUserSession } from './types/next-auth';
import type { EmployeeSessionInfoApiResponse } from './types';

export const EMPTY_IDP_ELIGIBILITY: IdpEligibilityResult = {
  eligible: false,
  sources: [],
  allowedActions: [],
};

const LEGACY_FULL_IDP_ELIGIBILITY: IdpEligibilityResult = {
  eligible: true,
  sources: [],
  allowedActions: [
    IdpEligibilityAction.LIST,
    IdpEligibilityAction.CREATE,
    IdpEligibilityAction.UPDATE,
  ],
};

type LegacyEmployeeSessionInfo = EmployeeSessionInfoApiResponse & {
  idpPermission?: boolean;
};

export function getEffectiveEmployeeId(
  user?: LcmUserSession['user']
): string | undefined {
  return user?.proxiedAs?.employeeId ?? user?.employeeId;
}

export function isSelfEmployeeMutation(
  user?: LcmUserSession['user'],
  targetEmployeeId?: string
): boolean {
  if (!targetEmployeeId) {
    return false;
  }

  return getEffectiveEmployeeId(user) === targetEmployeeId;
}

export function resolveIdpEligibilityFromAbilities(
  abilities: LegacyEmployeeSessionInfo
): IdpEligibilityResult {
  if (abilities.idpEligibility) {
    return abilities.idpEligibility;
  }

  if (abilities.idpPermission === true) {
    return LEGACY_FULL_IDP_ELIGIBILITY;
  }

  return EMPTY_IDP_ELIGIBILITY;
}

export function getIdpEligibilityFromUser(
  user?: LcmUserSession['user']
): IdpEligibilityResult {
  return (
    user?.proxiedAs?.idpEligibility ??
    user?.idpEligibility ??
    EMPTY_IDP_ELIGIBILITY
  );
}

export function canPerformIdpAction(
  idpEligibility: IdpEligibilityResult,
  action: IdpEligibilityAction
): boolean {
  return idpEligibility.allowedActions.includes(action);
}

export function hasIdpEligibilitySource(
  idpEligibility: IdpEligibilityResult,
  source: EligibilitySource
): boolean {
  return idpEligibility.sources.includes(source);
}

export function canViewIdp(idpEligibility: IdpEligibilityResult): boolean {
  return canPerformIdpAction(idpEligibility, IdpEligibilityAction.LIST);
}

export class IdpEligibilityGuardError extends Error {
  constructor() {
    super(IdpEligibilityErrorEnum.NOT_ELIGIBLE_FOR_ACTION);
    this.name = 'IdpEligibilityGuardError';
  }
}

export function assertCanPerformIdpAction(
  idpEligibility: IdpEligibilityResult,
  action: IdpEligibilityAction
): void {
  if (!canPerformIdpAction(idpEligibility, action)) {
    throw new IdpEligibilityGuardError();
  }
}
